// src/components/AgentJobCard.js
import React, { useEffect, useState, useRef } from 'react';
import styles from './AgentJobCard.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCog,
  faCheckCircle,
  faExclamationCircle,
  faExternalLinkAlt,
  faSync,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { useJobs } from '../context/jobContext';

const AgentJobCard = ({ jobId, onResume }) => {
  const { agentJobHistory, fetchAgentJobDetails, fetchAgentJobHistory } =
    useJobs();

  const [job, setJob] = useState(null);
  // UNIFIED STATE: We only need one variable to track if we are looking for the job
  const [isSearching, setIsSearching] = useState(true);
  const attemptRef = useRef(0);

  useEffect(() => {
    let isMounted = true;
    const POLL_INTERVAL = 2000; // 2 seconds
    const MAX_ATTEMPTS = 15; // Try for 30 seconds total (increased for safety)

    const resolveJob = async () => {
      // 1. Try to find in the GLOBAL context list first
      const found = agentJobHistory.find((j) => j.id === parseInt(jobId));

      if (found) {
        if (isMounted) {
          setJob(found);
          setIsSearching(false); // CRITICAL: Stop searching immediately
        }

        // --- RESUME LOGIC ---
        const isTerminalState = ['completed', 'finished', 'failed'].includes(
          found.status,
        );
        if (isTerminalState && onResume) {
          onResume(found.id);
        }
        return; // Found it, exit recursion
      }

      // 2. If NOT found, and we still have attempts left:
      if (attemptRef.current < MAX_ATTEMPTS) {
        if (isMounted) setIsSearching(true);
        console.log(
          `[AgentJobCard] Job #${jobId} missing. Refreshing History (Attempt ${attemptRef.current + 1})...`,
        );

        attemptRef.current += 1;

        // Trigger a background refresh of the full list
        // Note: We await this so we don't spam the API faster than it can respond
        await fetchAgentJobHistory();

        // Wait and try again (Recursive via timeout)
        if (isMounted) {
          setTimeout(resolveJob, POLL_INTERVAL);
        }
      } else {
        // 3. Max attempts reached, give up
        if (isMounted) {
          console.warn(
            `[AgentJobCard] Gave up looking for Job #${jobId} after ${MAX_ATTEMPTS} attempts.`,
          );
          setIsSearching(false); // CRITICAL: Stop searching so we can show the Error UI
        }
      }
    };

    resolveJob();

    return () => {
      isMounted = false;
    };
  }, [agentJobHistory, jobId, onResume, fetchAgentJobHistory]);

  const handleManualRetry = (e) => {
    e.preventDefault();
    attemptRef.current = 0; // Reset counter
    setIsSearching(true); // Restart search UI
    fetchAgentJobHistory(); // Kickstart the process
  };

  // --- RENDER LOGIC ---

  // 1. Syncing State
  if (isSearching && !job) {
    return (
      <div className={styles.loadingCard}>
        <FontAwesomeIcon icon={faSearch} beatFade />
        <span style={{ marginLeft: '8px' }}>Locating Job #{jobId}...</span>
      </div>
    );
  }

  // 2. Error State (Not Found after retries)
  if (!job) {
    return (
      <div className={styles.errorCard}>
        <FontAwesomeIcon icon={faExclamationCircle} />
        Job #{jobId} failed to start.
        <div className={styles.subText}>
          The Agent may have hallucinated this ID. Please ask it to try again.
        </div>
      </div>
    );
  }

  // 3. Success State (Job Found)
  const getStatusIcon = () => {
    if (['completed', 'finished'].includes(job.status)) return faCheckCircle;
    if (['failed', 'error', 'canceled'].includes(job.status))
      return faExclamationCircle;
    return faCog;
  };

  return (
    <div className={`${styles.card} ${styles[job.status]}`}>
      <div className={styles.header}>
        <span className={styles.title}>{job.jobName || `Job #${job.id}`}</span>
        <span className={`${styles.badge} ${styles[job.status]}`}>
          <FontAwesomeIcon
            icon={getStatusIcon()}
            spin={['running', 'queued', 'pending'].includes(job.status)}
          />
          {job.status}
        </span>
      </div>
      <div className={styles.body}>
        <div className={styles.message}>
          {job.statusMessage || 'Initializing...'}
        </div>
      </div>
      <div className={styles.footer}>
        <button
          onClick={(e) => {
            e.preventDefault();
            fetchAgentJobDetails(job.id, job.jobType);
          }}
          className={styles.detailsBtn}
        >
          View Trace <FontAwesomeIcon icon={faExternalLinkAlt} />
        </button>
      </div>
    </div>
  );
};

export default AgentJobCard;
